// Turns a menu-theme id into a set of inline CSS custom properties that paint a
// whole page's shadcn primitives (Card / Button / Badge / Input …) plus the
// page canvas. Shared by every customer-facing surface that should carry a
// branch's chosen look , the public branch page (app/b/[slug]) and the
// checkout page (app/pay/[orderId]) , so they theme identically.
//
// Pure and isomorphic: safe in both Server Components and Client Components
// (getMenuTheme resolves from the static themes.json registry; the color/font
// helpers are pure). Returns `{}` when there is no theme, so callers fall back
// to the app's default chrome with a single truthiness check.

import type { CSSProperties } from 'react';

import { resolveReadableTextColor } from '@/app/dashboard/templates/components/lib/templateExperience';
import { menuThemeFontValue } from './apply';
import { getMenuTheme, resolveMenuThemeColors, type MenuThemeMode } from './registry';
import { toHslTriplet } from './hsl';

export type MenuThemePageStyle = {
  /** Inline style for the page root: shadcn tokens + canvas paint. Undefined when no theme. */
  style?: CSSProperties;
  headingFont?: string;
  bodyFont?: string;
  accent?: string;
  hero?: string;
};

export function buildMenuThemePageStyle(
  themeId: string | null | undefined,
  requestedMode?: string | null,
): MenuThemePageStyle {
  const theme = getMenuTheme(themeId ?? null);
  if (!theme) return {};

  const mode: MenuThemeMode =
    requestedMode === 'dark' || requestedMode === 'light' ? requestedMode : theme.defaultMode;
  const { colors } = resolveMenuThemeColors(theme, mode);

  const primaryFg = resolveReadableTextColor(colors.primary, '#ffffff');
  const accentFg = resolveReadableTextColor(colors.accent, '#ffffff');
  const bg = colors.gradients?.background || colors.background;

  const style: CSSProperties = {
    // All of these MUST be HSL triplets , see toHslTriplet above.
    '--background': toHslTriplet(colors.background),
    '--foreground': toHslTriplet(colors.text),
    '--card': toHslTriplet(colors.surface),
    '--card-foreground': toHslTriplet(colors.text),
    '--popover': toHslTriplet(colors.surface),
    '--popover-foreground': toHslTriplet(colors.text),
    '--primary': toHslTriplet(colors.primary),
    '--primary-foreground': toHslTriplet(primaryFg),
    '--secondary': toHslTriplet(colors.secondary),
    '--secondary-foreground': toHslTriplet(colors.text),
    '--muted': toHslTriplet(colors.secondary),
    '--muted-foreground': toHslTriplet(colors.muted),
    '--accent': toHslTriplet(colors.accent),
    '--accent-foreground': toHslTriplet(accentFg),
    '--border': toHslTriplet(colors.divider),
    '--input': toHslTriplet(colors.divider),
    // `--input-background` is consumed directly (not via hsl()), so it keeps
    // the raw colour.
    '--input-background': colors.surface,
    '--ring': toHslTriplet(colors.accent),
    // Destructive/status red is NOT carried by menu palettes, so define it
    // here from the theme mode. Without this, error text falls back to the
    // Menuraq [data-theme] --destructive on <html> and leaks the platform
    // identity into a branch's own surface.
    '--destructive': mode === 'dark' ? '356 69% 55%' : '356 69% 45%',
    '--destructive-foreground': mode === 'dark' ? '60 24% 96%' : '42 54% 98%',
    background: bg,
    color: colors.text,
    colorScheme: mode === 'dark' ? 'dark' : 'light',
    fontFamily: menuThemeFontValue(theme.fonts.body),
    minHeight: '100vh',

    // ── The semantic layer, declared here too. This is load-bearing. ─────────
    //
    // styles/design-system.css derives `--color-bg-surface: hsl(var(--card))`
    // and friends AT `:root`. CSS custom properties resolve where they are
    // DECLARED, so redeclaring `--card` on a descendant — which is exactly what
    // every line above does — does NOT recompute `--color-bg-surface`. It keeps
    // the value it got from `:root`, i.e. the PLATFORM's palette.
    //
    // The result was silent and everywhere: a branch with a dark theme got a
    // correct dark `--card`, and a header styled `bg-[var(--color-bg-surface)]`
    // still painted itself the platform's cream — under text that had correctly
    // inherited the theme's near-white `color`. White on cream, unreadable, on
    // /reserve, /pay, /b, /receipt, /loyalty and /quote.
    //
    // doc 12 §2.1 says theme switching is automatic. That is true for the
    // `[data-theme]` blocks, which live on :root. It is NOT true for a per-page
    // inline theme like this one, and nothing said so until it shipped.
    '--color-bg-base': colors.background,
    '--color-bg-surface': colors.surface,
    '--color-bg-surface-hover': `color-mix(in srgb, ${colors.text} 8%, ${colors.surface})`,
    '--color-bg-sunken': colors.secondary,
    '--color-border-subtle': colors.divider,
    '--color-border-default': colors.divider,
    '--color-text-primary': colors.text,
    '--color-text-secondary': colors.muted,
    '--color-accent': colors.accent,
    '--color-accent-text': accentFg,
  } as CSSProperties;

  return {
    style,
    headingFont: menuThemeFontValue(theme.fonts.display || theme.fonts.heading),
    bodyFont: menuThemeFontValue(theme.fonts.body),
    accent: colors.accent,
    hero: colors.hero,
  };
}
